import { ReactNode } from 'react';

import { UserRole } from 'app-types/core';
import {
  Analytics,
  Bell,
  Dashboard,
  DeskIcon,
  ExchangeV2,
  HelpFilled,
  History,
  InvoiceChecking,
  Notifications,
  Orders,
  PickingList,
  Shipping,
  Tracking,
} from 'icons';

import {
  EnumSettingRoutes,
  GeneralRoutes,
  InvoiceCheckingTabRoutes,
  OrdersTabRoutes,
  ReturnsTabRoutes,
} from '../constants';

export enum NavbarMenuGroup {
  shipping = 'shipping',
  postShipping = 'postShipping',
  secondaryMenu = 'secondaryMenu',
  mobile = 'mobile',
}

export interface NavbarMenuItemData {
  id: MenuPages;
  to: string;
  icon: ReactNode;
  roles: UserRole[];
  langKey: string;
  groups: NavbarMenuGroup[];
  divider?: boolean;
  warning?: boolean;
  showFor: 'oldUI' | 'newUI' | 'both';
}

export const v2Path = '/v2';

export enum MenuPages {
  orders = 'orders',
  picking = 'picking',
  shipping = 'shipping',
  tracking = 'tracking',
  returns = 'returns',
  history = 'history',
  courierSupport = 'courierSupport',
  emailsSent = 'emailsSent',
  desk = 'desk',
  alerts = 'alerts',
  dashboard = 'dashboard',
  analytics = 'analytics',
  userSupport = 'userSupport',
  invoice = 'invoice',
}

const menu: NavbarMenuItemData[] = [
  {
    id: MenuPages.orders,
    to: '/cms/orders',
    icon: <Orders />,
    roles: Object.values(UserRole),
    langKey: 'general.orders',
    groups: [NavbarMenuGroup.shipping],
    showFor: 'oldUI',
  },
  {
    id: MenuPages.orders,
    to: `${v2Path}/${OrdersTabRoutes.orders}`,
    icon: <Orders />,
    roles: Object.values(UserRole),
    langKey: 'general.orders',
    groups: [NavbarMenuGroup.shipping],
    showFor: 'newUI',
  },
  {
    id: MenuPages.picking,
    to: '/cms/picking-list',
    icon: <PickingList />,
    roles: [UserRole.Owner, UserRole.Administrator, UserRole.Manager, UserRole.Warehouse],
    langKey: 'general.picking-list',
    groups: [NavbarMenuGroup.shipping],
    showFor: 'oldUI',
  },
  {
    id: MenuPages.shipping,
    to: '/cms/shipping-queue',
    icon: <Shipping />,
    roles: [UserRole.Owner, UserRole.Administrator, UserRole.Manager, UserRole.Warehouse],
    langKey: 'general.shipping-queue',
    groups: [NavbarMenuGroup.shipping],
    showFor: 'oldUI',
  },
  {
    id: MenuPages.tracking,
    to: '/cms/tracking',
    icon: <Tracking />,
    roles: Object.values(UserRole),
    langKey: 'general.tracking',
    groups: [NavbarMenuGroup.postShipping],
    showFor: 'oldUI',
  },
  {
    id: MenuPages.tracking,
    to: `${v2Path}/${EnumSettingRoutes.tracking}`,
    icon: <Tracking />,
    roles: Object.values(UserRole),
    langKey: 'general.tracking',
    groups: [NavbarMenuGroup.postShipping],
    showFor: 'newUI',
  },
  {
    id: MenuPages.returns,
    to: '/cms/returns',
    icon: <ExchangeV2 />,
    roles: Object.values(UserRole),
    langKey: 'general.returns',
    groups: [NavbarMenuGroup.postShipping],
    showFor: 'oldUI',
  },
  {
    id: MenuPages.returns,
    to: `${v2Path}/${ReturnsTabRoutes.returns}`,
    icon: <ExchangeV2 />,
    roles: Object.values(UserRole),
    langKey: 'general.returns',
    groups: [NavbarMenuGroup.postShipping],
    divider: true,
    showFor: 'newUI',
  },
  {
    id: MenuPages.history,
    to: '/cms/history',
    icon: <History />,
    roles: Object.values(UserRole),
    langKey: 'general.history',
    groups: [NavbarMenuGroup.postShipping],
    showFor: 'oldUI',
  },
  // {
  //   id: MenuPages.courierSupport,
  //   to: '/cms/courier-support',
  //   icon: <CourierSupport />,
  //   roles: [UserRole.Owner, UserRole.Administrator, UserRole.Manager, UserRole.CustomerSupport],
  //   langKey: 'general.courierSupport',
  //   groups: [NavbarMenuGroup.postShipping],
  //   showFor: 'both',
  // },
  {
    id: MenuPages.emailsSent,
    to: `${v2Path}/${GeneralRoutes.emails}/${GeneralRoutes.sent}`,
    icon: <Notifications />,
    roles: [UserRole.Owner, UserRole.Administrator, UserRole.Manager, UserRole.CustomerSupport],
    langKey: 'general.sentEmails',
    groups: [NavbarMenuGroup.postShipping],
    showFor: 'newUI',
  },
  {
    id: MenuPages.emailsSent,
    to: '/cms/emails/sent',
    icon: <Notifications />,
    roles: [UserRole.Owner, UserRole.Administrator, UserRole.Manager, UserRole.CustomerSupport],
    langKey: 'general.sentEmails',
    groups: [NavbarMenuGroup.postShipping],
    showFor: 'oldUI',
  },
  {
    id: MenuPages.desk,
    to: `${v2Path}/desk`,
    icon: <DeskIcon />,
    roles: [UserRole.Owner, UserRole.Administrator, UserRole.Manager, UserRole.CustomerSupport],
    langKey: 'general.outvioDesk',
    groups: [NavbarMenuGroup.postShipping],
    showFor: 'both',
  },
  {
    id: MenuPages.alerts,
    to: '/cms/notifications',
    icon: <Bell />,
    roles: [UserRole.Owner, UserRole.Administrator, UserRole.Manager],
    langKey: 'general.alerts',
    groups: [NavbarMenuGroup.mobile],
    showFor: 'both',
  },
  {
    id: MenuPages.dashboard,
    to: `${v2Path}/dashboard`,
    icon: <Dashboard />,
    roles: [UserRole.Owner, UserRole.Administrator, UserRole.Manager, UserRole.Warehouse],
    langKey: 'general.dashboard',
    groups: [NavbarMenuGroup.mobile],
    showFor: 'both',
  },
  {
    id: MenuPages.analytics,
    to: '/cms/analytics',
    icon: <Analytics />,
    roles: [UserRole.Owner, UserRole.Administrator, UserRole.Manager],
    langKey: 'general.analytics',
    groups: [NavbarMenuGroup.secondaryMenu],
    showFor: 'oldUI',
  },
  {
    id: MenuPages.analytics,
    to: `${v2Path}/analytics`,
    icon: <Analytics />,
    roles: [UserRole.Owner, UserRole.Administrator, UserRole.Manager],
    langKey: 'general.analytics',
    groups: [NavbarMenuGroup.secondaryMenu],
    showFor: 'newUI',
  },
  {
    id: MenuPages.userSupport,
    to: '/cms/user-support',
    icon: <HelpFilled />,
    roles: Object.values(UserRole),
    langKey: 'general.supportCenter',
    groups: [NavbarMenuGroup.mobile],
    showFor: 'both',
  },
  {
    id: MenuPages.invoice,
    to: `${v2Path}/${InvoiceCheckingTabRoutes.invoice}`,
    icon: <InvoiceChecking width={24} height={24} />,
    roles: [UserRole.Owner, UserRole.Administrator, UserRole.Manager],
    langKey: 'dashboard.invoiceChecking',
    groups: [NavbarMenuGroup.secondaryMenu],
    showFor: 'newUI',
  },
  {
    id: MenuPages.invoice,
    to: '/cms/invoice',
    icon: <InvoiceChecking width={24} height={24} />,
    roles: [UserRole.Owner, UserRole.Administrator, UserRole.Manager],
    langKey: 'dashboard.invoiceChecking',
    groups: [NavbarMenuGroup.secondaryMenu],
    showFor: 'oldUI',
  },
];

export interface NavbarDataByUserResponse {
  getByGroup: (menuGroup: NavbarMenuGroup[]) => NavbarMenuItemData[];
  getById: (pageId: MenuPages) => NavbarMenuItemData | undefined;
}

export function getNavbarDataByUser({
  roles = [],
}: {
  roles?: UserRole[];
  isMobile?: boolean;
}): NavbarDataByUserResponse {
  if (!roles.length) {
    return {
      getByGroup: () => [],
      getById: () => undefined,
    };
  }

  const _menu = menu.filter((item) => {
    const res = item.roles.some((role) => roles.includes(role));

    return res;
  });

  return {
    getByGroup: (menuGroup: NavbarMenuGroup[]) => {
      return _menu.filter((item) => item.groups.some((g) => menuGroup.includes(g)));
    },
    getById: (id) => {
      return _menu.find((item) => item.id === id);
    },
  };
}
